import React, { useMemo, type ReactNode } from 'react';
import OpinionEventSettingToolbar from '@intelligence/v3/components/OpinionBarAndLineChart/OpinionEventSettingToolbar';
import { useOpinionChartEventsWithDateLimit } from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/useOpinionChartEventsWithDateLimit';
import { buildEventClusterGameIds } from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/utils/buildEventClusterGameIds';
import { buildOpinionEventSettingStorageKey } from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/utils/opinionChartEventSettingStorage';

/** 收入 Tab：Platform 与 Revenue Scope 之间的事件筛选器占位 key */
export const MOBILE_STATS_OPINION_EVENT_FILTER_KEY = 'mobile_stats_opinion_event';

const MOBILE_STATS_OPINION_EVENT_PAGE_IDS = new Set([
  'activeusers',
  'revenue',
  'downloads',
  'rpd',
]);

export function isMobileStatsOpinionEventPage(pageId?: string): boolean {
  if (!pageId) {
    return false;
  }
  if (MOBILE_STATS_OPINION_EVENT_PAGE_IDS.has(pageId)) {
    return true;
  }
  return pageId.includes('retention') && pageId.includes('monthly');
}

function getOpinionChartXAxisData(
  trendLineList: Array<{
    time?: string;
    history?: Array<{ date?: string; time?: string }>;
  }>,
): string[] {
  if (!Array.isArray(trendLineList) || trendLineList.length === 0) {
    return [];
  }

  const firstItem = trendLineList[0];
  if (firstItem?.time) {
    return trendLineList
      .map(item => item?.time)
      .filter(Boolean)
      .map(date => String(date));
  }

  const history = firstItem?.history;
  if (Array.isArray(history) && history.length > 0) {
    return history
      .map(item => item?.date || item?.time)
      .filter(Boolean)
      .map(date => String(date));
  }

  return [];
}

export function renderOpinionEventToolbar(toolbar: ReactNode) {
  if (!toolbar) {
    return null;
  }

  return (
    <div className="dbi-mobile-stats-opinion-event-toolbar">{toolbar}</div>
  );
}

interface IUseMobileStatsOpinionEventsParams {
  pageId?: string;
  tabType?: string;
  isCoutriesPage?: boolean;
  gameId?: string;
  combineGameIdList?: Array<{ id?: string }>;
  entityType?: string;
  fetchParams: {
    start_date?: string;
    end_date?: string;
    date_type?: string;
  };
  trendLineList: Array<{ time?: string; history?: Array<{ date?: string }> }>;
}

export function useMobileStatsOpinionEvents(
  params: IUseMobileStatsOpinionEventsParams,
) {
  const {
    pageId,
    tabType,
    isCoutriesPage,
    gameId,
    combineGameIdList,
    entityType,
    fetchParams,
    trendLineList,
  } = params;

  const eventClusterGameIds = useMemo(
    () => buildEventClusterGameIds(combineGameIdList, gameId),
    [combineGameIdList, gameId],
  );

  const settingStorageKey = useMemo(() => {
    if (!gameId || !pageId) {
      return undefined;
    }

    return buildOpinionEventSettingStorageKey('stats', gameId, pageId);
  }, [gameId, pageId]);

  const enableOpinionEvents = useMemo(() => {
    return (
      isMobileStatsOpinionEventPage(pageId) &&
      tabType === 'stats' &&
      !isCoutriesPage
    );
  }, [pageId, tabType, isCoutriesPage]);

  // Mount the toolbar with the other filters instead of waiting for async dates.
  // The event request still waits until all required parameters are available.
  const enableOpinionEventRequest =
    enableOpinionEvents &&
    eventClusterGameIds.length > 0 &&
    !!fetchParams.start_date &&
    !!fetchParams.end_date;

  const {
    eventData,
    chartEventSetting,
    changeEventSetting,
    loading: eventLoading,
  } = useOpinionChartEventsWithDateLimit({
    gameIds: eventClusterGameIds,
    settingStorageKey,
    startTime: fetchParams.start_date || '',
    endTime: fetchParams.end_date || '',
    dateType: fetchParams.date_type,
    entityType,
    enabled: enableOpinionEventRequest,
  });

  const opinionChartXAxisData = useMemo(
    () => getOpinionChartXAxisData(trendLineList || []),
    [trendLineList],
  );

  const opinionEventToolbar = useMemo(() => {
    if (!enableOpinionEvents) {
      return null;
    }

    return (
      <OpinionEventSettingToolbar
        eventData={eventData}
        chartEventSetting={chartEventSetting}
        onEventSettingChange={changeEventSetting}
        xAxisData={opinionChartXAxisData}
        dateType={fetchParams.date_type}
        startTime={fetchParams.start_date}
        endTime={fetchParams.end_date}
      />
    );
  }, [
    enableOpinionEvents,
    eventData,
    chartEventSetting,
    changeEventSetting,
    opinionChartXAxisData,
    fetchParams.date_type,
    fetchParams.start_date,
    fetchParams.end_date,
  ]);

  const controlledEventAnnotation = enableOpinionEvents
    ? {
        eventData,
        chartEventSetting,
        changeEventSetting,
        eventLoading,
        settingStorageKey,
      }
    : undefined;

  return {
    enableOpinionEvents,
    controlledEventAnnotation,
    opinionEventToolbar,
    settingStorageKey,
  };
}
